import { type ReactElement } from 'react';
import { ArrowLeft, Plug } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { FetchErrorCallout } from '@/components/ui/fetch-error-callout';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { PageHeader } from '@/components/shell/PageHeader';
import { useAdminFhirIntegration } from '@/features/admin-fhir-integrations/integrationApi';
import { IdentifierMappingsSection } from '@/features/admin-fhir-integrations/components/IdentifierMappingsSection';
import { MappedFieldsSection } from '@/features/admin-fhir-integrations/components/MappedFieldsSection';

/**
 * Per-integration detail view (PTV2-1473) at /admin/fhir-integrations/:guid. Re-homes the identifier
 * mappings and mapped-field allow-list — dropped from the routed UI when the entry-based list replaced
 * the old single-config page (PTV2-1434) — as child config of one selected integration entry. The
 * entry's own launch knobs, credentials and Auth0 settings are edited from the list's form dialog; this
 * page owns only the two per-integration child resources.
 */
export function AdminFhirIntegrationDetailPage(): ReactElement {
  const { t } = useTranslation();
  const { guid = '' } = useParams<{ guid: string }>();
  const entryQuery = useAdminFhirIntegration(guid);

  const base = 'routes.admin.fhirIntegrations.entries.detail';
  const backLink = (
    <Button variant="outline" size="sm" asChild>
      <Link to="/admin/fhir-integrations">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        {t(`${base}.backToList`)}
      </Link>
    </Button>
  );

  return (
    <>
      <PageHeader
        icon={<Plug className="h-[18px] w-[18px]" aria-hidden="true" />}
        title={entryQuery.data?.name ?? t('routes.admin.fhirIntegrations.heading')}
        subtitle={t(`${base}.subheading`)}
        actions={backLink}
      />
      <div className="space-y-8 bg-background px-8 py-5">
        {entryQuery.isPending && <Skeleton className="h-9 w-full max-w-sm rounded-md" aria-hidden="true" />}

        {entryQuery.isError && (
          <div className="max-w-xl">
            <FetchErrorCallout
              error={entryQuery.error}
              heading={t(`${base}.error.heading`)}
              message={t(`${base}.error.message`)}
              codeLabel={t(`${base}.error.codeLabel`)}
              onRetry={() => {
                entryQuery.refetch().catch(() => undefined);
              }}
              retryLabel={t(`${base}.error.retry`)}
            />
          </div>
        )}

        {entryQuery.isSuccess && (
          <>
            <IdentifierMappingsSection integrationGuid={guid} />
            <Separator />
            <MappedFieldsSection integrationGuid={guid} />
          </>
        )}
      </div>
    </>
  );
}
